import './style.scss';
import { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Redirect } from "react-router-dom"; 
import AuthService from '../../../shared/authService';
import QueryService from '../../../shared/queryService';
import Utils from '../../../shared/utils';
import ConfigService from '../../../shared/configService';
import { IFeature } from '@esri/arcgis-rest-types';
import IReportMainFeature from './models/IReportMainFeature';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes} from '@fortawesome/free-solid-svg-icons';
import { IEditReportState, IMetadataModalityTab, IRestorationPledgeTab, ISuccessFactorsFinancialFlow, ISuccessFactorsMonitoringSystem, ISuccessFactorsPlanningMethodology, ISuccessFactorsPolicy, ISuccessFactorsTab, IResultsAndBenefitsItem, ISocioEconomicImpactItem, IResultsAndBenefitsTab, ISocioEconomicImpactsTab } from './models/IEditReportState';
import { IRefTierFeature } from './models/IRefFeature';
import ModalitiesTab from './templates/ModalitiesTab';
import RestorationPledge from './templates/RestorationPledge';
import SuccessFactors from './templates/SuccessFactors';
import ResultsAndBenefits from './templates/ResultsAndBenefits';
import ActivitiesUpload from './templates/ActivitiesUpload';
import { ToastContainer, toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import LoadingOverlay from 'react-loading-overlay';
import { IRelatedRecordGroup } from '@esri/arcgis-rest-feature-layer';
import { IRefFocalPointFeature } from './models/IRefFeature';
const lookup = require('country-code-lookup');

const EditReport = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const reportId: number = Number(urlParams.get('id'));
  const history = useHistory();
  const {t} = useTranslation('reports');
  let tiers: IRefTierFeature[] = [];

  // Confirm dialog
  const [exitDialog, setExitDialog] = useState(false);
  const [submitReportDialog, setSubmitReportDialog] = useState(false);

  /**
   * Handle exit dialog
   */
   const handleExitDialogClose = () => setExitDialog(false);
   const handleExitDialogShow = () => setExitDialog(true);
   const handleExitConfirm = () => {
    goToReportsListPage();
    handleExitDialogClose();
   }

   /**
   * Handle submit report dialog
   */
  const handleSubmitReportDialogClose = () => setSubmitReportDialog(false);
  const handleSubmitReportDialogShow = () => setSubmitReportDialog(true);
  const handleSubmitReportConfirm = () => {
    onSubmit();
    handleSubmitReportDialogClose();
  }

  /**
   * Go to reports list page
   */
  const goToReportsListPage = () => {
    history.push(ConfigService.getConfig().routes?.submissionTool.reports || '');
  }

  /**
   * State
   */
   const [localState, setLocalState] = useState({
    reportLayerIndex: ConfigService.getConfig().agol?.operationalLayers.reportMain.index,
    reportRBLayerIndex: ConfigService.getConfig().agol?.operationalLayers.reportRb.index,
    activitiesPointsLayerIndex: ConfigService.getConfig().agol?.operationalLayers.activitiesPoints.index,
    drafBtnMouseOver: false,
    submitBtnMouseOver: false,
    isSaveDraftLoading: false,
    isSubmitLoading: false,
    isFetchingReportData: false,
   });

   const [state, setState] = useState<IEditReportState>({
    userLoaded: AuthService.checkConnectedUser(),
    reportLoaded: false,
    report: null,

    metadataModalityTab: {
      isActive: true,
      isOK: false,
      emailOK: false,
      successFactorPoliciesInstitOn: false,
      successFactorFinancialFlowOn: false,
      successFactorTechPlanningOn: false,
      successFactorMonitoringOn: false,
      benefitsHectaresRestorOn: true,
      benefitsClimateImpactOn: false,
      benefitsBiodivImpactOn: false,
      benefitsSocioEcoImpactOn: false
    },
  
    restorationPledgeTab: {
      isActive: false,
      isOK: false
    },

    successFactorsTab: {
      isActive: false,
      isOK: false,
      isDisabled: false,
      policiesAndInstitTab: {
        policies: [],
        deleteObjetIds: []
      },
      financialFlowsTab: {
        totalDomesticPublicExpenditure: {
          report_id: '', 
          type: ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalDomesticPublicExpenditureType
        },
        totalPrivateInvestments: {
          report_id: '', 
          type: ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalPrivateInvestmentsType
        },
        totalInternationalDonorSupport: {
          report_id: '', 
          type: ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalInternationalDonorSupportType
        },
        totalDomesticPhilanthropicAndNonProfit: {
          report_id: '', 
          type: ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalDomesticPhilanthropicAndNonProfitType
        }
      },
      technicalPlanningTab: {
        planningMethodologies: [],
        deleteObjetIds: []
      },
      monitoringTab: {
        monitoringSystems: [],
        deleteObjetIds: []
      }
    },

    resultsAndBenefitsTab: {
      isActive: false,
      isOK: false,
      isGlobalMode: true,
      resultsAndBenefitsItems: []
    },

    activitiesUploadTab: {
      isActive: false,
      isOK: false,
      reportHasShapeUploaded: false,
      restoredHectares:  0,
      investments:  0,
      cO2Sequestred:  0,
      jobsCreated:  0,
      activitiesFound:  0,
      activitiesAssociatedToDistrict:  0,
      activitiesFallingInReportZone:  0,
      activitiesFallingOutsideReportZone:  0,
      alreadyUploadedShapefileFeatures: [],
      alreadyUploadedShapefileFeaturesType: '',
      newUploadedShapefileFeatures: [],
      shapefileUploadDate: ''
      ,reportingEntityType:0
    }
  });

  /**
   * Report data fetch
   */
  const [fetchReportData, setFetchReportData] = useState({});
  useEffect(() => {
    getReport();
  }, [fetchReportData]);

  useEffect(() => {
    enableDisableFactorsTab();
  }, [state.metadataModalityTab?.report_type, 
    state.metadataModalityTab?.successFactorFinancialFlowOn,
    state.metadataModalityTab?.successFactorMonitoringOn,
    state.metadataModalityTab?.successFactorPoliciesInstitOn,
    state.metadataModalityTab?.successFactorTechPlanningOn]
  );
  
  /**
   * Get related features
   * @param url 
   * @param relationshipId 
   * @param objectId 
   */
  const getRelatedFeatures = async (url: string, relationshipId: number, objectId: number): Promise<IFeature[]> => {
    try {
      const relPoliciesRecords: IRelatedRecordGroup[] = await QueryService.queryRelatedFeatures(url, relationshipId, [objectId]);
      
      if(relPoliciesRecords && relPoliciesRecords.length) {
        const relatedRecord: IRelatedRecordGroup | undefined = relPoliciesRecords.find((item: IRelatedRecordGroup) => item.objectId === objectId);

        if(relatedRecord) {
          return Promise.resolve(relatedRecord.relatedRecords || []);
        }
      }

      return Promise.resolve([]);
    } catch(err: any) {
      console.error(err);
      return Promise.reject(err);
    }
  }
  
  /**
   * Set fetching report data loading on / off
   * @param loading
   */
   const setFetchingReportDataLoading = (loading: boolean) => {
    const obj: any = { ...localState };

    if(obj) {
      obj.isFetchingReportData = loading;
      setLocalState(obj);
    }
  }

  /**
   * Set tiers list
   * @returns
   */
  const setTiersList = async () => {
    try {
      const tierFeatures: IFeature[] = await QueryService.queryReferenceFeatures(ConfigService.getConfig().agol?.referenceTables.REF_TIERS.index);

      tiers = tierFeatures.map((feat: IFeature) => feat.attributes as IRefTierFeature);
      return Promise.resolve();
    }
    catch(err: any) {
      return Promise.reject();
    }
  }

  /**
   * Get reports list
   */
  const getReport = async () => {
    try {
      if(state.userLoaded) {

        setFetchingReportDataLoading(true);

        // 1) Get layer infos
        await QueryService.getLayerFieldsInfos(localState.reportLayerIndex);
        await QueryService.getLayerFieldsInfos(localState.activitiesPointsLayerIndex);
        
        // 2) Set tiers
        await setTiersList();
        
        // 3) Get the report
        const features: IFeature[] = await QueryService.queryFeatures(Utils.getOpLayerQuery(localState.reportLayerIndex), `${ConfigService.getConfig().agol?.operationalLayers.reportMain.fields.objectid} = ${reportId}`, false, '*', `${ConfigService.getConfig().agol?.operationalLayers.reportMain.fields.baselineYear} DESC`);
        
        if(features && features.length) {
          //4) Get related features
          const objectId: number = features[0].attributes[ConfigService.getConfig().agol?.operationalLayers.reportMain.fields.objectid];

          // 4.1) Report policies          
          const relPoliciesFeatures: IFeature[] = await getRelatedFeatures(Utils.getOpLayerQuery(localState.reportLayerIndex), ConfigService.getConfig().agol?.operationalLayers.reportMain.relationships.reportPolicies, objectId);
          
          // 4.2) Report financial flows
          const financialFlowsFeatures: IFeature[] = await getRelatedFeatures(Utils.getOpLayerQuery(localState.reportLayerIndex), ConfigService.getConfig().agol?.operationalLayers.reportMain.relationships.reportFinancialFlows, objectId);

          // 4.3) Report technical planning
          const technicalPlanningFeatures: IFeature[] = await getRelatedFeatures(Utils.getOpLayerQuery(localState.reportLayerIndex), ConfigService.getConfig().agol?.operationalLayers.reportMain.relationships.reportTechnicalPlanning, objectId);
          
          // 4.4) Report monitoring
          const monitoringFeatures: IFeature[] = await getRelatedFeatures(Utils.getOpLayerQuery(localState.reportLayerIndex), ConfigService.getConfig().agol?.operationalLayers.reportMain.relationships.reportMonitoring, objectId);

          //5) Results and benefits
          
          // 5.1) RB
          const rbFeatures: IFeature[] = await getRelatedFeatures(Utils.getOpLayerQuery(localState.reportLayerIndex), ConfigService.getConfig().agol?.operationalLayers.reportMain.relationships.reportRb, objectId);

          // 5.2) RB jobs
          const rbJobs: any = await getRelatedJobs(rbFeatures);

          // Fill data
          fillData(features[0] as IReportMainFeature, relPoliciesFeatures, financialFlowsFeatures, technicalPlanningFeatures, monitoringFeatures, rbFeatures, rbJobs);

          setFetchingReportDataLoading(false);
        } else {
          fillData(null);
        }
      }
    } catch(err: any) {
      setFetchingReportDataLoading(false);
      console.error(err);
    }
  };

  /**
   * Get related result benefits jobs
   * @param rbFeatures
   * @returns
   */
  const getRelatedJobs = async (rbFeatures: IFeature[]) => {
    try {
      const rbJobs: any = {};

      if (rbFeatures && rbFeatures.length) {
        const res = await Promise.all(rbFeatures.map( async (feat: IFeature) => {
          return await getRelatedFeatures(Utils.getOpLayerQuery(localState.reportRBLayerIndex),
          ConfigService.getConfig().agol?.operationalLayers.reportMain.relationships.reportRbJobs, 
          feat.attributes.OBJECTID);
        }));

        if (res && res.length) {
          rbFeatures.forEach( async (feat: IFeature, i: number) => {
            if (res[i]) {
              rbJobs[feat.attributes.GlobalID] = res[i];
            }
          });
        }
      }

      return Promise.resolve(rbJobs);
    }
    catch(err: any) {
      return Promise.reject(err);
    }
  }

  /**
   * Get success factors policie from features
   * @param features
   * @returns
   */
  const getSuccessFactorsPolicies = (features: IFeature[]): ISuccessFactorsPolicy[] => {
    return features.map( (feat: IFeature, i: number) => {
      return {
        isActive: i === 0,
        OBJECTID: feat.attributes.OBJECTID,
        report_id: feat.attributes.report_id,
        name: feat.attributes.name,
        year_enacted: feat.attributes.year_enacted,
        year_editing: feat.attributes.year_editing,
        ha_targeted: feat.attributes.ha_targeted,
        budget_alloc: feat.attributes.budget_alloc,
        responsible: feat.attributes.responsible,
        links: feat.attributes.links,
        information: feat.attributes.information
      }
    });
  }

  /**
   * Get success factors policie from features
   * @param report 
   * @param features 
   * @returns 
   */
   const getSuccessFactorsFinancialFlows = (report: IReportMainFeature | null, features: IFeature[]): {
    totalDomesticPublicExpenditure?: ISuccessFactorsFinancialFlow,
    totalPrivateInvestments?: ISuccessFactorsFinancialFlow,
    totalInternationalDonorSupport?: ISuccessFactorsFinancialFlow,
    totalDomesticPhilanthropicAndNonProfit?: ISuccessFactorsFinancialFlow
   } => {
     const financialFlows: {
      totalDomesticPublicExpenditure?: ISuccessFactorsFinancialFlow,
      totalPrivateInvestments?: ISuccessFactorsFinancialFlow,
      totalInternationalDonorSupport?: ISuccessFactorsFinancialFlow,
      totalDomesticPhilanthropicAndNonProfit?: ISuccessFactorsFinancialFlow
     } = {};
    
     features.forEach( (feat: IFeature, i: number) => {
      const type: string = feat.attributes.type;
      
      const atts: any = {
        isActive: i === 0,
        OBJECTID: feat.attributes.OBJECTID,
        type: feat.attributes.type,
        report_id: feat.attributes.report_id,
        amount: feat.attributes.amount,
        critical_species_fund_percent: feat.attributes.critical_species_fund_percent,
        tier: feat.attributes.tier,
        sources: feat.attributes.sources
      };

      switch (type) {
        case ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalDomesticPublicExpenditureType:
          financialFlows.totalDomesticPublicExpenditure = atts;
          break;
        
        case ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalPrivateInvestmentsType:
          financialFlows.totalPrivateInvestments = atts;
          break;

        case ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalInternationalDonorSupportType:
          financialFlows.totalInternationalDonorSupport = atts;
          break;

        case ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalDomesticPhilanthropicAndNonProfitType:
          financialFlows.totalDomesticPhilanthropicAndNonProfit = atts;
          break;
      }
    });

    // Default values if not defined
    if (!financialFlows.totalDomesticPublicExpenditure) {
      financialFlows.totalDomesticPublicExpenditure = {
        report_id: (report as any)?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID],
        type: ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalDomesticPublicExpenditureType,
        amount: 0,
        tier: tiers.length ? (tiers[0] as IRefTierFeature).key_ : ''
      }
    }

    if (!financialFlows.totalPrivateInvestments) {
      financialFlows.totalPrivateInvestments = {
        report_id: (report as any)?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID],
        type: ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalPrivateInvestmentsType,
        amount: 0,
        tier: tiers.length ? (tiers[0] as IRefTierFeature).key_ : ''
      }
    }

    if (!financialFlows.totalInternationalDonorSupport) {
      financialFlows.totalInternationalDonorSupport = {
        report_id: (report as any)?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID],
        type: ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalInternationalDonorSupportType,
        amount: 0,
        tier: tiers.length ? (tiers[0] as IRefTierFeature).key_ : ''
      }
    }

    if (!financialFlows.totalDomesticPhilanthropicAndNonProfit) {
      financialFlows.totalDomesticPhilanthropicAndNonProfit = {
        report_id: (report as any)?.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.reportID],
        type: ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.totalDomesticPhilanthropicAndNonProfitType,
        amount: 0,
        tier: tiers.length ? (tiers[0] as IRefTierFeature).key_ : ''
      }
    }

    return financialFlows;
  }

  /**
   * Get success factors planning methodologies from features
   * @param features
   * @returns
   */
   const getSuccessFactorsPlanningMethodologies = (features: IFeature[]): ISuccessFactorsPlanningMethodology[] => {
    return features.map( (feat: IFeature, i: number) => {
      return {
        isActive: i === 0,
        OBJECTID: feat.attributes.OBJECTID,
        report_id: feat.attributes.report_id,
        methodology: feat.attributes.methodology,
        description: feat.attributes.description,
        scale: feat.attributes.scale,
        obs_scale: feat.attributes.obs_scale,
        landscape_status: feat.attributes.landscape_status,
        explanation: feat.attributes.explanation,
        species_adressed: feat.attributes.species_adressed,
        details: feat.attributes.details,
        links: feat.attributes.links,
        information: feat.attributes.information
      }
    })
  }

  /**
   * Get success factors monitoring systems from features
   * @param features
   * @returns
   */
   const getSuccessFactorsMonitoringSystems = (features: IFeature[]): ISuccessFactorsMonitoringSystem[] => {
    return features.map( (feat: IFeature, i: number) => {
      return {
        isActive: i === 0,
        OBJECTID: feat.attributes.OBJECTID,
        report_id: feat.attributes.report_id,
        monitoring_system: feat.attributes.monitoring_system,
        description: feat.attributes.description,
        link: feat.attributes.link,
        managing_instit: feat.attributes.managing_instit,
        geo_scale: feat.attributes.geo_scale,
        track_list: feat.attributes.track_list,
        other: feat.attributes.other,
        mrv_system: feat.attributes.mrv_system,
        information: feat.attributes.information,
        links: feat.attributes.links
      }
    })
  }

  /**
   * Get results and benefits from features
   * @param features
   * @param rbJobs
   * @returns
   */
   const geResultsAndBenefits = (features: IFeature[], rbJobs: any = {}): IResultsAndBenefitsItem[] => {
    return features.map( (feat: IFeature, i: number) => {
      const objectId: number = feat.attributes.OBJECTID;
      const globalId: string = feat.attributes.GlobalID;
      
      const outRb: IResultsAndBenefitsItem = {
        isActive: i === 0,
        isOK: false,
        OBJECTID: objectId,
        report_id: feat.attributes.report_id,
        name0: feat.attributes.NAME_0,
        gid0: feat.attributes.GID_0,
        name1: feat.attributes.NAME_1,
        gid1: feat.attributes.GID_1,
        name2: feat.attributes.NAME_2,
        gid2: feat.attributes.GID_2,

        hectaresUnderRestorationTab: {
          isOK: false,
          ha_intervention_types: feat.attributes.ha_intervention_types,
          ha_under_resto: feat.attributes.ha_under_resto,
          ha_imp_year: feat.attributes.ha_imp_year,
          ha_tier: feat.attributes.ha_tier,
          ha_links: feat.attributes.ha_links,
          ha_information: feat.attributes.ha_information
        },

        climateImpactsTab: {
          isOK: false,
          co2_sequestered: feat.attributes.co2_sequestered,
          co2_systems: feat.attributes.co2_systems,
          co2_tier: feat.attributes.co2_tier,
          co2_other: feat.attributes.co2_other,
          co2_contribution: feat.attributes.co2_contribution,
          co2_links: feat.attributes.co2_links,
          co2_information: feat.attributes.co2_information
        },

        biodiversityImpactsTab: {
          isOK: false,
          bio_ha_threat_species: feat.attributes.bio_ha_threat_species,
          bio_protarea_ha: feat.attributes.bio_protarea_ha,
          bio_nativearea_ha: feat.attributes.bio_nativearea_ha,
          bio_primarea_ha: feat.attributes.bio_primarea_ha,
          bio_intactarea_ha: feat.attributes.bio_intactarea_ha,
          bio_redlist: feat.attributes.bio_redlist,
          bio_links: feat.attributes.bio_links,
          bio_information: feat.attributes.bio_information
        }
      };
      
      // socioEconomicImpacts
      if(rbJobs && rbJobs[globalId]) {
        const relatedJobs: IFeature[] = rbJobs[globalId];
        
        outRb.socioEconomicImpactsTab = {
          isOK: false
        }

        relatedJobs.forEach( (feat: IFeature) => {
          const ecoType: string = feat.attributes[ConfigService.getConfig().agol.operationalLayers.reportRbJobs.fields.ecoType];
          
          switch (ecoType) {
            case ConfigService.getConfig().agol.operationalLayers.reportRbJobs.seasonalCasualOccasionalType:
              outRb.socioEconomicImpactsTab!.seasonalCasualOccasional = feat.attributes as ISocioEconomicImpactItem;
            break;

            case ConfigService.getConfig().agol.operationalLayers.reportRbJobs.shortTermType:
              outRb.socioEconomicImpactsTab!.shortTerm = feat.attributes as ISocioEconomicImpactItem;
            break;

            case ConfigService.getConfig().agol.operationalLayers.reportRbJobs.longTermType:
              outRb.socioEconomicImpactsTab!.longTerm = feat.attributes as ISocioEconomicImpactItem;
            break;

            case ConfigService.getConfig().agol.operationalLayers.reportRbJobs.unspecifiedType:
              outRb.socioEconomicImpactsTab!.unspecified = feat.attributes as ISocioEconomicImpactItem;
            break;
          }
        });

        // 1) seasonalCasualOccasional
        if(!outRb.socioEconomicImpactsTab.seasonalCasualOccasional) {
          outRb.socioEconomicImpactsTab.seasonalCasualOccasional = {
            OBJECTID: -1,
            report_rb_id: globalId,
            eco_type: ConfigService.getConfig().agol.operationalLayers.reportRbJobs.seasonalCasualOccasionalType
          };
        }

        // 2) shortTerm
        if(!outRb.socioEconomicImpactsTab.shortTerm) {
          outRb.socioEconomicImpactsTab!.shortTerm = {
            OBJECTID: -1,
            report_rb_id: globalId,
            eco_type: ConfigService.getConfig().agol.operationalLayers.reportRbJobs.shortTermType
          };
        }

        // 3) longTerm
        if(!outRb.socioEconomicImpactsTab.longTerm) {
          outRb.socioEconomicImpactsTab!.longTerm = {
            OBJECTID: -1,
            report_rb_id: globalId,
            eco_type: ConfigService.getConfig().agol.operationalLayers.reportRbJobs.longTermType
          };
        }

        // 4 ) unspecified
        if(!outRb.socioEconomicImpactsTab.unspecified) {
          outRb.socioEconomicImpactsTab!.unspecified = {
            OBJECTID: -1,
            report_rb_id: globalId,
            eco_type: ConfigService.getConfig().agol.operationalLayers.reportRbJobs.unspecifiedType
          };
        }
      }

      return outRb;
    });
  }

  /**
   * Fill data
   * @param report
   * @param relPoliciesFeatures
   * @param financialFlowsFeatures
   */
  const fillData = (
    report: IReportMainFeature | null, 
    relPoliciesFeatures: IFeature[] = [],
    financialFlowsFeatures: IFeature[] = [],
    technicalPlanningFeatures: IFeature[] = [], 
    monitoringFeatures: IFeature[] = [],
    rbFeatures: IFeature[] = [],
    rbJobs: any = {}
    )=> {
    const reportState: IEditReportState = { ...state }; 
    
    if(report) {     
      reportState.report = report;

      // 1) Metatdata / Modalities
      fillMetatdataModalitiesData(report);

      // 2) Restoration pledge
      fillRestorationPledgeData(report);

      // 3) Success factors
      fillSuccessFactorsData(report, relPoliciesFeatures, financialFlowsFeatures, technicalPlanningFeatures, monitoringFeatures);

      // 4) Results and benefits
      fillResultsAndBenefits(rbFeatures, rbJobs);
    }

    // Is report loaded
    reportState.reportLoaded = true;
    
    setState(reportState);
  }

  /**
   * Fill Metatdata / Modalities Data
   * @param report 
   * @param relPoliciesFeatures 
   */
  const fillMetatdataModalitiesData = (report: IReportMainFeature | null)=> {
    const reportState: IEditReportState = { ...state }; 
    
    if(report) {     
      reportState.metadataModalityTab!.reportingCountry = report.attributes.NAME_0;
      reportState.metadataModalityTab!.reportingState = report.attributes.NAME_1;
      reportState.metadataModalityTab!.rep_year = report.attributes.rep_year;
      reportState.metadataModalityTab!.coord_gvt = report.attributes.coord_gvt;
      reportState.metadataModalityTab!.focal_point_name = report.attributes.focal_point_name;
      reportState.metadataModalityTab!.designation = report.attributes.designation;
      reportState.metadataModalityTab!.focal_point_email = report.attributes.focal_point_email;
      reportState.metadataModalityTab!.phone = report.attributes.phone;
      reportState.metadataModalityTab!.email = report.attributes.email;
      reportState.metadataModalityTab!.report_type = report.attributes.report_type || ConfigService.getConfig().agol.referenceTables.REF_MODALITY.standardValue;
      reportState.metadataModalityTab!.report_process = report.attributes.report_process;
      reportState.metadataModalityTab!.report_parts = report.attributes.report_parts;

      reportState.metadataModalityTab!.successFactorPoliciesInstitOn = !report.attributes.report_parts || (report.attributes.report_parts && report.attributes.report_parts.split(',').indexOf('1') != -1 ? true : false);
      reportState.metadataModalityTab!.successFactorFinancialFlowOn = !report.attributes.report_parts || (report.attributes.report_parts && report.attributes.report_parts.split(',').indexOf('2') != -1 ? true : false);
      reportState.metadataModalityTab!.successFactorTechPlanningOn = !report.attributes.report_parts || (report.attributes.report_parts && report.attributes.report_parts.split(',').indexOf('3') != -1 ? true : false);
      reportState.metadataModalityTab!.successFactorMonitoringOn = !report.attributes.report_parts || (report.attributes.report_parts && report.attributes.report_parts.split(',').indexOf('4') != -1 ? true : false);
      reportState.metadataModalityTab!.benefitsHectaresRestorOn = true;
      reportState.metadataModalityTab!.benefitsClimateImpactOn = !report.attributes.report_parts || (report.attributes.report_parts && report.attributes.report_parts.split(',').indexOf('6') != -1 ? true : false);
      reportState.metadataModalityTab!.benefitsBiodivImpactOn = !report.attributes.report_parts || (report.attributes.report_parts && report.attributes.report_parts.split(',').indexOf('7') != -1 ? true : false);
      reportState.metadataModalityTab!.benefitsSocioEcoImpactOn = !report.attributes.report_parts || (report.attributes.report_parts && report.attributes.report_parts.split(',').indexOf('8') != -1 ? true : false);
      
      if(report.attributes.GID_0) {
        const countryInfos: any = lookup.byIso(report.attributes.GID_0);

        if(countryInfos) {
          reportState.metadataModalityTab!.countryCode = countryInfos.iso2.toLowerCase();
        }
      }
    }
  }

  /**
   * Fill restoration pledge data
   * @param report
   */
  const fillRestorationPledgeData = (report: IReportMainFeature | null)=> {
    const reportState: IEditReportState = { ...state }; 
    
    if(report) {     
      reportState.restorationPledgeTab!.ha_pledged = report.attributes.ha_pledged;
      reportState.restorationPledgeTab!.baseline_year = report.attributes.baseline_year;
      reportState.restorationPledgeTab!.pledge_start = report.attributes.pledge_start;
      
      if(report.attributes.pledge_end) {
        reportState.restorationPledgeTab!.pledge_end = new Date(report.attributes.pledge_end).getTime();
      }
      
      reportState.restorationPledgeTab!.links = report.attributes.links;
      reportState.restorationPledgeTab!.linked_init = report.attributes.linked_init;
      reportState.restorationPledgeTab!.linked_init_other = report.attributes.linked_init_other;
      reportState.restorationPledgeTab!.ecosystems = report.attributes.ecosystems;
      reportState.restorationPledgeTab!.ecosystems_other = report.attributes.ecosystems_other;

      validateRestorationPledgeData(reportState.restorationPledgeTab);
    }
  }

  /**
   * Fill succes factors data
   * @param report
   * @param relPoliciesFeatures
   */
   const fillSuccessFactorsData = (
    report: IReportMainFeature | null, 
    relPoliciesFeatures: IFeature[] = [],
    financialFlowsFeatures: IFeature[] = [],
    technicalPlanningFeatures: IFeature[] = [], 
    monitoringFeatures: IFeature[] = []
   )=> {
    const reportState: IEditReportState = { ...state }; 
    
    if(report) { 
      // 1) Policies
      if (relPoliciesFeatures && relPoliciesFeatures.length) {
        const successFactorsPolicies: ISuccessFactorsPolicy[] = getSuccessFactorsPolicies(relPoliciesFeatures);
        reportState.successFactorsTab!.policiesAndInstitTab.policies = successFactorsPolicies;
        reportState.successFactorsTab!.policiesAndInstitTab.deleteObjetIds = successFactorsPolicies ? successFactorsPolicies?.map( (p: ISuccessFactorsPolicy) => { return p.OBJECTID; }) : [];
      }

      // 2) FinancialFlows
      if (financialFlowsFeatures) {
        const financialFlows = getSuccessFactorsFinancialFlows(report, financialFlowsFeatures);
        reportState.successFactorsTab!.financialFlowsTab.totalDomesticPublicExpenditure = financialFlows.totalDomesticPublicExpenditure;
        reportState.successFactorsTab!.financialFlowsTab.totalPrivateInvestments = financialFlows.totalPrivateInvestments;
        reportState.successFactorsTab!.financialFlowsTab.totalInternationalDonorSupport = financialFlows.totalInternationalDonorSupport;
        reportState.successFactorsTab!.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit = financialFlows.totalDomesticPhilanthropicAndNonProfit;
      }

      // 3) Technical Planning
      if (technicalPlanningFeatures && technicalPlanningFeatures.length) {
        const planningMethodologies: ISuccessFactorsPlanningMethodology[] = getSuccessFactorsPlanningMethodologies(technicalPlanningFeatures);
        reportState.successFactorsTab!.technicalPlanningTab.planningMethodologies = planningMethodologies;
        reportState.successFactorsTab!.technicalPlanningTab.deleteObjetIds = planningMethodologies ? planningMethodologies?.map( (p: ISuccessFactorsPlanningMethodology) => { return p.OBJECTID; }) : [];
      }

      // 4) Monitoring
      if (monitoringFeatures && monitoringFeatures.length) {
        const monitoringSystems: ISuccessFactorsMonitoringSystem[] = getSuccessFactorsMonitoringSystems(monitoringFeatures);
        reportState.successFactorsTab!.monitoringTab.monitoringSystems = monitoringSystems;
        reportState.successFactorsTab!.monitoringTab.deleteObjetIds = monitoringSystems ? monitoringSystems?.map( (p: ISuccessFactorsMonitoringSystem) => { return p.OBJECTID; }) : [];
      }
    }

    validateSuccessFactorsData(reportState.successFactorsTab, reportState.metadataModalityTab);
  }

  /**
   * Fill succes factors data
   * @param report
   * @param rbFeatures
   */
  const fillResultsAndBenefits = (rbFeatures: IFeature[] = [], rbJobs: any = {}) => {
    const reportState: IEditReportState = { ...state }; 

    if (rbFeatures && rbFeatures.length) {
      let resultsAndBenefits: IResultsAndBenefitsItem[] = geResultsAndBenefits(rbFeatures, rbJobs);
      if (resultsAndBenefits.length > 1) {
        resultsAndBenefits = resultsAndBenefits.filter( (i: IResultsAndBenefitsItem) => i.gid1 || i.gid2);
      }
      reportState.resultsAndBenefitsTab!.resultsAndBenefitsItems = resultsAndBenefits;
      reportState.resultsAndBenefitsTab!.isGlobalMode = reportState.resultsAndBenefitsTab!.resultsAndBenefitsItems.length < 2;
    }

    validateResultsAndBenefitsTab(reportState.resultsAndBenefitsTab!, reportState.metadataModalityTab);
  }

  /**
   * Enable / disable success factors tab
   */
  const enableDisableFactorsTab = () => {
    const reportState: IEditReportState = { ...state }; 
    
    reportState.successFactorsTab!.isDisabled = reportState.metadataModalityTab && 
    reportState.metadataModalityTab.report_type === ConfigService.getConfig().agol.referenceTables.REF_MODALITY.snapshotValue &&
    !reportState.metadataModalityTab.successFactorFinancialFlowOn &&
    !reportState.metadataModalityTab.successFactorMonitoringOn &&
    !reportState.metadataModalityTab.successFactorPoliciesInstitOn &&
    !reportState.metadataModalityTab.successFactorTechPlanningOn ? true : false;
    
    setState(reportState);
  }

  /**
   * Select tab
   */
   const selectTab = (index: number) => {
    const obj: IEditReportState = {...state}; 

    obj.metadataModalityTab!.isActive = index === 0;
    obj.restorationPledgeTab!.isActive = index === 1;
    obj.successFactorsTab!.isActive = index === 2;
    obj.resultsAndBenefitsTab!.isActive = index === 3;
    obj.activitiesUploadTab!.isActive = index === 4;
    
    setState(obj);
  };

  /**
   * Handle save draft hover
   */
  const draftMouseOver = (hovered: boolean) => {
    const obj: any = { ...localState };
    obj.drafBtnMouseOver = hovered;
    setLocalState(obj);
  }

  /**
   * Handle submit hover
   */
   /*const submittMouseOver = (hovered: boolean) => {
    const obj: any = { ...localState };
    obj.submitBtnMouseOver = hovered;
    setLocalState(obj);
  }*/

  /**
   * Validate metadataModality data
   * @param metadataModalityTab 
   */
   const validateMetadataModalityData = (metadataModalityTab: IMetadataModalityTab | undefined): void => {
    // Check mandatory fields
    let isOK = metadataModalityTab ? Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportMain.requiredFields.metadataModality, metadataModalityTab) : false;
    
    // Check mail format
    const email: string | undefined = metadataModalityTab?.email;
    
    if(email) {
      const isMailOK: boolean = Utils.validateMail(email);
      isOK = isOK && isMailOK;
      metadataModalityTab!.emailOK = isMailOK;
    }

    metadataModalityTab!.isOK = isOK;
  }

  /**
   * Validate restoration pledge data
   * @param restorationPledgeTab 
   */
   const validateRestorationPledgeData = (restorationPledgeTab: IRestorationPledgeTab | undefined): void => {
    // Check mandatory fields
    restorationPledgeTab!.isOK = restorationPledgeTab ? Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportMain.requiredFields.restorationPledge, restorationPledgeTab) : false;
  }

  /**
   * Validate success factors data
   * @param successFactorsTab 
   * @param metadataModalityTab 
   */
   const validateSuccessFactorsData = (successFactorsTab: ISuccessFactorsTab | undefined, metadataModalityTab: IMetadataModalityTab | undefined): void => {
    let isOK = true;

    if(metadataModalityTab && successFactorsTab) {

      // 1) Report policies
      if (metadataModalityTab.successFactorPoliciesInstitOn && successFactorsTab.policiesAndInstitTab.policies.length) {
        successFactorsTab.policiesAndInstitTab.policies.forEach( (i: ISuccessFactorsPolicy) => {
          isOK = isOK && Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportPolicies.requiredFields, i);

          if (!isOK) {
            return false;
          }
        });
      }
      
          
      // 2) Report financial flows
      if (isOK && metadataModalityTab.successFactorFinancialFlowOn && successFactorsTab.financialFlowsTab) {
        if (isOK) {
          isOK = isOK && Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.requiredFields, successFactorsTab.financialFlowsTab.totalDomesticPublicExpenditure);
        }

        if (isOK) {
          isOK = isOK && Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.requiredFields, successFactorsTab.financialFlowsTab.totalPrivateInvestments);
        }
        
        if (isOK) {
          isOK = isOK && Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.requiredFields, successFactorsTab.financialFlowsTab.totalInternationalDonorSupport);
        }
        
        if (isOK) {
          isOK = isOK && Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportFinancialFlows.requiredFields, successFactorsTab.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit);
        }
      }

      // 3) Report technical planning
      if (isOK && metadataModalityTab.successFactorTechPlanningOn && successFactorsTab.technicalPlanningTab.planningMethodologies.length) {
        successFactorsTab.technicalPlanningTab.planningMethodologies.forEach( (i: ISuccessFactorsPlanningMethodology) => {
          isOK = isOK && Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportTechnicalPlanning.requiredFields, i);

          if (!isOK) {
            return false;
          }
        });
      }
      
      // 4) Report monitoring
      if (isOK && metadataModalityTab.successFactorMonitoringOn && successFactorsTab.monitoringTab.monitoringSystems.length) {
        successFactorsTab.monitoringTab.monitoringSystems.forEach( (i: ISuccessFactorsMonitoringSystem) => {
          isOK = isOK && Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportMonitoring.requiredFields, i);

          if (!isOK) {
            return false;
          }
        });
      }
    }

    successFactorsTab!.isOK = isOK;
  }

  /**
   * Validate results and benefits tab
   * @param item 
   * @returns 
   */
   const validateResultsAndBenefitsTab = (rb: IResultsAndBenefitsTab, metadataModalityTab: IMetadataModalityTab | undefined): void => {
    let isOK = true;

    if(rb.resultsAndBenefitsItems && rb.resultsAndBenefitsItems.length) {
      rb.resultsAndBenefitsItems.forEach( (item: IResultsAndBenefitsItem) => {
        item.hectaresUnderRestorationTab.isOK = !metadataModalityTab?.benefitsHectaresRestorOn || Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportRb.requiredFields.hectaresUnderRestoration, item.hectaresUnderRestorationTab);
        item.climateImpactsTab.isOK =  !metadataModalityTab?.benefitsClimateImpactOn || Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportRb.requiredFields.climateImpacts, item.climateImpactsTab);
        item.biodiversityImpactsTab.isOK = !metadataModalityTab?.benefitsBiodivImpactOn || Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportRb.requiredFields.biodiversityImpacts, item.biodiversityImpactsTab);
        item.socioEconomicImpactsTab!.isOK = !metadataModalityTab?.benefitsSocioEcoImpactOn || (Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportRbJobs.requiredFields, item.socioEconomicImpactsTab?.seasonalCasualOccasional) && 
                                            Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportRbJobs.requiredFields, item.socioEconomicImpactsTab?.shortTerm) &&
                                            Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportRbJobs.requiredFields, item.socioEconomicImpactsTab?.longTerm) &&
                                            Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportRbJobs.requiredFields, item.socioEconomicImpactsTab?.unspecified));

        item.isOK = (item.hectaresUnderRestorationTab.isOK && item.climateImpactsTab.isOK && item.biodiversityImpactsTab.isOK && item.socioEconomicImpactsTab?.isOK) || false;
        
        if (!item.isOK) {
          isOK = false;
        }
      });
    } else {
      isOK = false;
    }

    rb.isOK = isOK;
  }
  
  /**
   * Validate hectares under retoration tab
   * @param item 
   * @returns 
   */
  const validateHectaresUnderRestorationTab = (item: IResultsAndBenefitsItem): void => {
    item.hectaresUnderRestorationTab.isOK = Utils.checkRequiredFields(ConfigService.getConfig().agol.operationalLayers.reportRb.requiredFields.hectaresUnderRestoration, item.hectaresUnderRestorationTab);
    item.isOK = (item.hectaresUnderRestorationTab.isOK && item.climateImpactsTab.isOK && item.biodiversityImpactsTab.isOK && item.socioEconomicImpactsTab?.isOK) || false;
  }
  
  /**
   * Get metadata / modalities attributes
   * @returns
   */
  const getMetadataModalitiesAtts = (): any => {
    const atts:any = {};

    if(state) {
      const tab: IMetadataModalityTab | undefined = state.metadataModalityTab;

      if (tab) {
        atts[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.objectid] = reportId;
        atts.coord_gvt = tab.coord_gvt;
        atts.focal_point_name = tab.focal_point_name;
        atts.designation = tab.designation; 
        atts.email = tab.email; 
        atts.phone = tab.phone;
        atts.focal_point_email = tab.focal_point_email;
        atts.report_type = tab.report_type;
        atts.report_parts = tab.report_parts;
        atts.report_process = tab.report_type === ConfigService.getConfig().agol.referenceTables.REF_MODALITY.standardValue ? tab.report_process : null;
      }
    }

    return atts;
  }

  /**
   * Get metadata / modalities attributes
   * @returns
   */
   const getRestorationPledgeAtts = (): any => {
    const atts:any = {};

    if(state) {
      const tab: IRestorationPledgeTab | undefined = state.restorationPledgeTab;

      if (tab) {
        atts.ha_pledged = tab.ha_pledged;
        atts.baseline_year = tab.baseline_year;
        atts.pledge_start = tab.pledge_start; 
        atts.pledge_end = tab.pledge_end; 
        atts.linked_init = tab.linked_init;
        atts.linked_init_other = tab.linked_init_other;
        atts.ecosystems = tab.ecosystems;
        atts.ecosystems_other = tab.ecosystems_other;
        atts.links = tab.links;
      }
    }

    return atts;
  }

  /**
   * Get success factors policies
   * @returns
   */
   const getSuccessFactorsPoliciesAtts = (): ISuccessFactorsPolicy[] => {
    let policies: any[] = [];

    if(state) {
      const tab: ISuccessFactorsTab | undefined = state.successFactorsTab;

      if (tab && tab.policiesAndInstitTab.policies) {
        policies = tab.policiesAndInstitTab.policies;
      }
    }

    return policies;
  }

  /**
   * Get success factors financial flows
   * @returns
   */
   const getSuccessFactorsFinancialFlowsAtts = (): ISuccessFactorsFinancialFlow[] => {
    let financialFlows: ISuccessFactorsFinancialFlow[] = [];

    if(state) {
      const tab: ISuccessFactorsTab | undefined = state.successFactorsTab;

      if (tab && tab.financialFlowsTab) {
        // 1) totalDomesticPublicExpenditure
        if(tab.financialFlowsTab.totalDomesticPublicExpenditure && tab.financialFlowsTab.totalDomesticPublicExpenditure.type) {
          tab.financialFlowsTab.totalDomesticPublicExpenditure.tier = tab.financialFlowsTab.totalDomesticPublicExpenditure.tier !== '-999' ? tab.financialFlowsTab.totalDomesticPublicExpenditure.tier : '';
          financialFlows.push(tab.financialFlowsTab.totalDomesticPublicExpenditure);
        }

        // 2) totalPrivateInvestments
        if(tab.financialFlowsTab.totalPrivateInvestments && tab.financialFlowsTab.totalPrivateInvestments.type) {
          tab.financialFlowsTab.totalPrivateInvestments.tier = tab.financialFlowsTab.totalPrivateInvestments.tier !== '-999' ? tab.financialFlowsTab.totalPrivateInvestments.tier : '';
          financialFlows.push(tab.financialFlowsTab.totalPrivateInvestments);
        }

        // 3) totalInternationalDonorSupport
        if(tab.financialFlowsTab.totalInternationalDonorSupport && tab.financialFlowsTab.totalInternationalDonorSupport.type) {
          tab.financialFlowsTab.totalInternationalDonorSupport.tier = tab.financialFlowsTab.totalInternationalDonorSupport.tier !== '-999' ? tab.financialFlowsTab.totalInternationalDonorSupport.tier : '';
          financialFlows.push(tab.financialFlowsTab.totalInternationalDonorSupport);
        }

        // 4) totalDomesticPhilanthropicAndNonProfit
        if(tab.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit && tab.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit.type) {
          tab.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit.tier = tab.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit.tier !== '-999' ? tab.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit.tier : '';
          financialFlows.push(tab.financialFlowsTab.totalDomesticPhilanthropicAndNonProfit);
        }
      }
    }

    return financialFlows;
  }

  /**
   * Get success factors planning methodologies
   * @returns
   */
   const getSuccessFactorsPlanningMethodologiesAtts = (): ISuccessFactorsPlanningMethodology[] => {
    let methodologies: any[] = [];

    if(state) {
      const tab: ISuccessFactorsTab | undefined = state.successFactorsTab;

      if (tab && tab.technicalPlanningTab.planningMethodologies) {
        methodologies =  tab.technicalPlanningTab.planningMethodologies;
      }
    }

    // Handle species_adressed checked or not and scale
    methodologies.map ( (i: ISuccessFactorsPlanningMethodology) => {
      if (i.species_adressed !== 1) {
        i.details = '';
      }

      i.scale = i.scale !== '-999' ? i.scale : '';
      i.obs_scale = i.obs_scale !== '-999' ? i.obs_scale : '';

      return i;
    });

    return methodologies;
  }  

  /**
   * Get success factors monitoring systems
   * @returns
   */
   const getSuccessFactorsMonitoringSystemsAtts = (): ISuccessFactorsMonitoringSystem[] => {
    let systems: any[] = [];

    if(state) {
      const tab: ISuccessFactorsTab | undefined = state.successFactorsTab;

      if (tab && tab.monitoringTab.monitoringSystems) {
        systems = tab.monitoringTab.monitoringSystems;
      }
    }

    // Handle mrv_system checked or not and scale
    systems.map ( (i: ISuccessFactorsMonitoringSystem) => {
      if (i.mrv_system !== 1) {
        i.information = '';
      }

      i.geo_scale = i.geo_scale !== '-999' ? i.geo_scale : '';

      return i;
    });

    return systems;
  }
  
  /**
   * Get results and benefits atts
   * @returns
   */
   const getResultsAndBenefitsAtts = (): any[] => {
    if(state) {
      const tab: IResultsAndBenefitsTab | undefined = state.resultsAndBenefitsTab;

      if (tab && tab.resultsAndBenefitsItems) {
        return tab.resultsAndBenefitsItems.map( (i:IResultsAndBenefitsItem) => {
          i.hectaresUnderRestorationTab.ha_tier = i.hectaresUnderRestorationTab.ha_tier !== '-999' ? i.hectaresUnderRestorationTab.ha_tier : '';
          i.climateImpactsTab.co2_tier = i.climateImpactsTab.co2_tier !== '-999' ? i.climateImpactsTab.co2_tier : '';

          // Sum of jobs created
          let jobsCreated = 0;
          jobsCreated += i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.seasonalCasualOccasional && i.socioEconomicImpactsTab.seasonalCasualOccasional.eco_jobs ? Number(i.socioEconomicImpactsTab.seasonalCasualOccasional.eco_jobs) : 0;
          jobsCreated += i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.shortTerm && i.socioEconomicImpactsTab.shortTerm.eco_jobs ? Number(i.socioEconomicImpactsTab.shortTerm.eco_jobs) : 0;
          jobsCreated += i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.longTerm && i.socioEconomicImpactsTab.longTerm.eco_jobs ? Number(i.socioEconomicImpactsTab.longTerm.eco_jobs) : 0;
          jobsCreated += i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.unspecified && i.socioEconomicImpactsTab.unspecified.eco_jobs ? Number(i.socioEconomicImpactsTab.unspecified.eco_jobs) : 0; 
          
          return { 
            ...{ OBJECTID: i.OBJECTID, report_id: i.report_id, GID_1: i.gid1, NAME_1: i.name1, GID_2: i.gid2, NAME_2: i.name2, eco_jobs_created: jobsCreated }, 
            ...i.hectaresUnderRestorationTab, 
            ...i.climateImpactsTab, 
            ...i.biodiversityImpactsTab 
          };
        });
      }
    }

    return [];
  }
  
  /**
   * Get results and benefits jobs atts
   * @returns
   */
   const getResultsAndBenefitsJobsAtts = (): {adds: ISocioEconomicImpactsTab[], updates: ISocioEconomicImpactsTab[]} => {
    const relatedJobs: {adds: ISocioEconomicImpactsTab[], updates: ISocioEconomicImpactsTab[]} = {adds: [], updates: []};

    if(state) {
      const tab: IResultsAndBenefitsTab | undefined = state.resultsAndBenefitsTab;

      if (tab && tab.resultsAndBenefitsItems) {
        tab.resultsAndBenefitsItems.forEach( (i:IResultsAndBenefitsItem) => {
          if (i && i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.seasonalCasualOccasional && i.socioEconomicImpactsTab?.seasonalCasualOccasional?.eco_unit === '-999') {
            i.socioEconomicImpactsTab.seasonalCasualOccasional.eco_unit = '';
          }
          
          if (i && i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.seasonalCasualOccasional && i.socioEconomicImpactsTab?.seasonalCasualOccasional?.eco_tiers === '-999') {
            i.socioEconomicImpactsTab.seasonalCasualOccasional.eco_tiers = '';
          }

          if (i && i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.shortTerm && i.socioEconomicImpactsTab?.shortTerm?.eco_unit === '-999') {
            i.socioEconomicImpactsTab.shortTerm.eco_unit = '';
          }

          if (i && i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.shortTerm && i.socioEconomicImpactsTab?.shortTerm?.eco_tiers === '-999') {
            i.socioEconomicImpactsTab.shortTerm.eco_tiers = '';
          }

          if (i && i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.longTerm && i.socioEconomicImpactsTab?.longTerm?.eco_unit === '-999') {
            i.socioEconomicImpactsTab.longTerm.eco_unit = '';
          }

          if (i && i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.longTerm && i.socioEconomicImpactsTab?.longTerm?.eco_tiers === '-999') {
            i.socioEconomicImpactsTab.longTerm.eco_tiers = '';
          }

          if (i && i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.unspecified && i.socioEconomicImpactsTab?.unspecified?.eco_unit === '-999') {
            i.socioEconomicImpactsTab.unspecified.eco_unit = '';
          }

          if (i && i.socioEconomicImpactsTab && i.socioEconomicImpactsTab.unspecified && i.socioEconomicImpactsTab?.unspecified?.eco_tiers === '-999') {
            i.socioEconomicImpactsTab.unspecified.eco_tiers = '';
          }

          const item: ISocioEconomicImpactsTab = {
            isOK: false,
            seasonalCasualOccasional: i.socioEconomicImpactsTab?.seasonalCasualOccasional,
            shortTerm: i.socioEconomicImpactsTab?.shortTerm,
            longTerm: i.socioEconomicImpactsTab?.longTerm,
            unspecified: i.socioEconomicImpactsTab?.unspecified
          };

          if (i.socioEconomicImpactsTab?.seasonalCasualOccasional?.OBJECTID && i.socioEconomicImpactsTab?.seasonalCasualOccasional?.OBJECTID !== -1) {
            relatedJobs.updates.push(item);
          } else {
            relatedJobs.adds.push(item);
          }
        });
      }
    }

    return relatedJobs;
  }

  /**
   * Call save data
   * @param isSubmitMode 
   * @returns 
   */
  const callSaveData = async(isSubmitMode: boolean = false): Promise<any> => {
    try {
      const metadataAtts = getMetadataModalitiesAtts();
      const restorationPledgeAtts = getRestorationPledgeAtts();
      const successFactorsPoliciesAtts = getSuccessFactorsPoliciesAtts();
      const successFactorsFinancialFlowsAtts = getSuccessFactorsFinancialFlowsAtts();
      const successFactorsPlanningMethodologiesAtts = getSuccessFactorsPlanningMethodologiesAtts();
      const successFactorsMonitoringSystemsAtts = getSuccessFactorsMonitoringSystemsAtts();
      const resultsAndBenefitsAtts = getResultsAndBenefitsAtts();
      const resultsAndBenefitsJobsAtts = getResultsAndBenefitsJobsAtts();
      
      // Report main atts
      const reportMainAtts = {attributes: { ...metadataAtts, ...restorationPledgeAtts}};

      if (isSubmitMode) {
        reportMainAtts.attributes[ConfigService.getConfig().agol.operationalLayers.reportMain.fields.submitted] = ConfigService.getConfig().agol.operationalLayers.reportMain.reportSubmittedCode;
      }

      const res = await QueryService.saveEdits(
        // 1) Report main
        {
          updates: [reportMainAtts]
        },

        // 2) Success factors
        {
          // 2.1) Policies
          policies: {
            adds: successFactorsPoliciesAtts && successFactorsPoliciesAtts.length ? 
            successFactorsPoliciesAtts.map( (i: ISuccessFactorsPolicy) => {
              return {attributes: i};
            }) : [],
            deletes: state.successFactorsTab && state.successFactorsTab.policiesAndInstitTab ? state.successFactorsTab.policiesAndInstitTab.deleteObjetIds : []
          },

          // 2.2) Financial flows
          financialFlows: {
            adds: successFactorsFinancialFlowsAtts.filter( (i: ISuccessFactorsFinancialFlow) => i.report_id && !i.OBJECTID).map( (item: ISuccessFactorsFinancialFlow) => { return {attributes: item};}),
            updates: successFactorsFinancialFlowsAtts.filter( (i: ISuccessFactorsFinancialFlow) => i.report_id && i.OBJECTID).map( (item: ISuccessFactorsFinancialFlow) => { return {attributes: item};})
          },

          // 2.3) Technical planning
          planningMethodologies: {
            adds: successFactorsPlanningMethodologiesAtts && successFactorsPlanningMethodologiesAtts.length ? 
            successFactorsPlanningMethodologiesAtts.map( (i: ISuccessFactorsPlanningMethodology) => {
              return {attributes: i};
            }) : [],
            deletes: state.successFactorsTab && state.successFactorsTab.technicalPlanningTab ? state.successFactorsTab.technicalPlanningTab.deleteObjetIds : []
          },

          // 2.4) Monitoring
          monitoringSystems: {
            adds: successFactorsMonitoringSystemsAtts && successFactorsMonitoringSystemsAtts.length ? 
            successFactorsMonitoringSystemsAtts.map( (i: ISuccessFactorsMonitoringSystem) => {
              return {attributes: i};
            }) : [],
            deletes: state.successFactorsTab && state.successFactorsTab.monitoringTab ? state.successFactorsTab.monitoringTab.deleteObjetIds : []
          }
        },

        // 3) Results and benefits
        {
          adds: resultsAndBenefitsAtts && resultsAndBenefitsAtts.length ? resultsAndBenefitsAtts.filter( (i: any) => !i.OBJECTID || i.OBJECTID < 0).map( (i: any) => { return {attributes: i}; }) : [],
          updates: resultsAndBenefitsAtts && resultsAndBenefitsAtts.length ? resultsAndBenefitsAtts.filter( (i: any) => i.OBJECTID && i.OBJECTID > 0).map( (i: any) => { return {attributes: i}; }) : [],
          deletes: state.resultsAndBenefitsTab ? state.resultsAndBenefitsTab.deleteRbObjetIds : [],
          addsJobs: resultsAndBenefitsJobsAtts.adds,
          updatesJobs: resultsAndBenefitsJobsAtts.updates,
          deletesJobs: state.resultsAndBenefitsTab ? state.resultsAndBenefitsTab.deleteRbJobsObjetIds : []
        }
      );

      return Promise.resolve(res);
    } catch(err: any) {
      console.error(err);
      return Promise.reject(err);
    }
  }

  /**
   * Handle save draft
   */
  const onSaveDraft = async() => {
    try {
      setSaveDraftLoading(true);
      const res: any = await callSaveData();
      setSaveDraftLoading(false);
      toast.success(t('newReport.saveDraftSuccess'));

      // Reload report
      setFetchReportData({});

    } catch (err: any) {
      setSaveDraftLoading(false);
      toast.error(t('newReport.saveDraftError'));
    }
  }

  /**
   * Set save draft loading on / off
   * @param loading
   */
  const setSaveDraftLoading = (loading: boolean) => {
    const obj: any = { ...localState };

    if(obj) {
      obj.isSaveDraftLoading = loading;
      obj.isSubmitLoading = false;
      setLocalState(obj);
    }
  }

  /**
   * Send submit mail
   */
  const sendSubmitEmail = async() => {
    try {
      const tab: IMetadataModalityTab | undefined = state.metadataModalityTab;
      const focalPointsFeatures: IFeature[] = await QueryService.queryReferenceFeatures(ConfigService.getConfig().agol?.referenceTables.REF_IUCN_FOCAL_POINT_EMAIL.index);
      const selectedFocalPoint: IRefFocalPointFeature | undefined = focalPointsFeatures.map((feat: IFeature) => feat.attributes as IRefFocalPointFeature).find((i:IRefFocalPointFeature) => i.key_ === tab?.focal_point_email);
      
      let countryState: string = state.report!.attributes[ConfigService.getConfig().agol?.operationalLayers.reportMain.fields.name0 || ''];
      countryState += state.report!.attributes[ConfigService.getConfig().agol?.operationalLayers.reportMain.fields.name1] ? ' - ' + state.report!.attributes[ConfigService.getConfig().agol?.operationalLayers.reportMain.fields.name1 || ''] : '';
      QueryService.sendSubmitMail(selectedFocalPoint?.value, countryState, state.report!.attributes[ConfigService.getConfig().agol?.operationalLayers.reportMain.fields.repYear || '']);
      
      return Promise.resolve();
    } catch (err: any) {
      console.log(err);
      return Promise.reject(err);
    }
  }

  /**
   * Handle submit
   */
  const onSubmit = async() => {
    try {
      setSubmitLoading(true);
      
      const res: any = await callSaveData(true);
      setSubmitLoading(false);
      toast.success(t('newReport.saveDraftSuccess'));

      // Send email
      await sendSubmitEmail();

      // Go to reports list page
      goToReportsListPage();

    } catch (err: any) {
      setSubmitLoading(false);
      toast.error(t('newReport.saveDraftError'));
    }
  }

   /**
   * Set submit loading on / off
   * @param loading
   */
    const setSubmitLoading = (loading: boolean) => {
      const obj: any = { ...localState };
  
      if(obj) {
        obj.isSaveDraftLoading = false;
        obj.isSubmitLoading = loading;
        setLocalState(obj);
      }
    }

  return(
    <>
    {state.userLoaded ?
        
      state.report && state.report.attributes ? 
      <div className="editReportDiv wrapper">

        {(state.report.attributes.published) ?
        <div className="editReportContent">
          <span className="textError">{t('newReport.alreadySubmittedPublishedReport')}</span>
        </div>
        
        :
        
        <div className="editReportContent">
          <div>
            <span className="pageTitle bigText">
              {state.report.attributes[ConfigService.getConfig().agol?.operationalLayers.reportMain.fields.name0 || '']}
              {state.report.attributes[ConfigService.getConfig().agol?.operationalLayers.reportMain.fields.name1 || ''] && ' - ' + state.report.attributes[ConfigService.getConfig().agol?.operationalLayers.reportMain.fields.name1 || '']}
              {' - '}
              {state.report.attributes[ConfigService.getConfig().agol?.operationalLayers.reportMain.fields.repYear || '']}
            </span>

            <div className='buttonsDiv'>
              <button className="actionBtn secondary" onClick={() => handleExitDialogShow()}>                        
                  <span>{t('newReport.close')}</span>
              </button>       
              
              <button className="actionBtn secondary" onClick={ onSaveDraft } onMouseOver={ () => draftMouseOver(true) } onMouseOut={ () => draftMouseOver(false) }>
                  <span>{t('newReport.saveDraft')}</span>
              </button>

              <button className="actionBtn primary" onClick={ handleSubmitReportDialogShow } disabled={!state || !state.metadataModalityTab?.isOK || !state.restorationPledgeTab?.isOK || !state.successFactorsTab?.isOK || !state.resultsAndBenefitsTab?.isOK}>
                  <span>{t('newReport.submit')}</span>
              </button>
            </div>            
          </div>

          <div className="tabs">

            <div className={`tabItem ${state.metadataModalityTab && state.metadataModalityTab?.isActive ? 'active' : state.metadataModalityTab && state.metadataModalityTab?.isOK ? 'ok' : 'default'}`}
            onClick={() => selectTab(0)}>
              <span className="tabSpan">{t('newReport.metadataModality.label')}</span>
              <FontAwesomeIcon className="tabIcon checkIcon" icon={ faCheck }/>
              <FontAwesomeIcon className="tabIcon closeIcon" icon={ faTimes }/>
            </div>

            <div className={`tabItem ${state.restorationPledgeTab && state.restorationPledgeTab?.isActive ? 'active' : state.restorationPledgeTab && state.restorationPledgeTab?.isOK ? 'ok' : 'default'}`}
            onClick={() => selectTab(1)}>
              <span className="tabSpan">{t('newReport.restorationPledge.label')}</span>
              <FontAwesomeIcon className="tabIcon checkIcon" icon={ faCheck }/>
              <FontAwesomeIcon className="tabIcon closeIcon" icon={ faTimes }/>
            </div>

            <div className={`tabItem ${state.successFactorsTab && state.successFactorsTab?.isActive ? 'active' : !state.successFactorsTab?.isActive && !state.successFactorsTab?.isDisabled && state.successFactorsTab?.isOK ? 'ok' : state.successFactorsTab && state.successFactorsTab?.isDisabled ? 'default disabled' : 'default'}`}
            onClick={() => selectTab(2)}>
              <span className="tabSpan">{t('newReport.successFactors.label')}</span>
              <FontAwesomeIcon className="tabIcon checkIcon" icon={ faCheck }/>
              <FontAwesomeIcon className="tabIcon closeIcon" icon={ faTimes }/>
            </div>

            <div className={`tabItem ${state.resultsAndBenefitsTab && state.resultsAndBenefitsTab?.isActive ? 'active' : state.resultsAndBenefitsTab && state.resultsAndBenefitsTab?.isOK ? 'ok' : 'default'}`}
            onClick={() => selectTab(3)}>
              <span className="tabSpan">{t('newReport.resultsAndBenefits.label')}</span>
              <FontAwesomeIcon className="tabIcon checkIcon" icon={ faCheck }/>
              <FontAwesomeIcon className="tabIcon closeIcon" icon={ faTimes }/>
            </div>

            <div className={`tabItem ${state.activitiesUploadTab && state.activitiesUploadTab?.isActive ? 'active' : state.activitiesUploadTab && state.activitiesUploadTab?.isOK ? 'ok' : 'default'}`}
            onClick={() => selectTab(4)}>
              <span className="tabSpan">{t('newReport.activitiesUpload.label')}</span>
            </div>
          </div>

          <div className="tabsContent" style={{backgroundImage: `url("${process.env.PUBLIC_URL}/assets/background/edit-report-bg.svg")`}}>
            {state.metadataModalityTab && state.metadataModalityTab?.isActive &&
            <div className="wrapper">
              <ModalitiesTab state={state} setState={setState} 
              validateData={validateMetadataModalityData}  
              validateSuccessFactorsData={validateSuccessFactorsData}
              validateResultsAndBenefitsTab={validateResultsAndBenefitsTab}/>
            </div>
            }
            
            {state.restorationPledgeTab && state.restorationPledgeTab?.isActive &&
            <div className="wrapper">
              <RestorationPledge state={state} setState={setState} validateData={validateRestorationPledgeData}/>
            </div>
            }

            {state.successFactorsTab && state.successFactorsTab?.isActive &&
            <div className="wrapper">
              <SuccessFactors state={state} setState={setState} validateData={validateSuccessFactorsData}/>
            </div>
            }

            {state.resultsAndBenefitsTab && state.resultsAndBenefitsTab?.isActive &&
            <div className="wrapper">
              <ResultsAndBenefits state={state} setState={setState} validateData={validateResultsAndBenefitsTab}/>
            </div>
            }

            {state.activitiesUploadTab && state.activitiesUploadTab?.isActive &&
            <div className="wrapper">
              <ActivitiesUpload state={state} setState={setState}/>
            </div>
            }
          </div>
        </div>
        }

        <ToastContainer 
          position="bottom-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover/>

        <Modal show={exitDialog} onHide={handleExitDialogClose}>
          <Modal.Header closeButton>
            <Modal.Title>{t('newReport.closeEditionTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{t('newReport.closeEditionContent')}</Modal.Body>
          <Modal.Footer>
            <button className="dialogBtn secondary" onClick={handleExitDialogClose}>
            {t('newReport.closeEditionCancel')}
            </button>
            <button className="dialogBtn primary" onClick={handleExitConfirm}>
            {t('newReport.closeEditionConfirm')}
            </button>
          </Modal.Footer>
        </Modal>

        <Modal show={submitReportDialog} onHide={handleSubmitReportDialogClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('newReport.confirmSubmitTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('newReport.confirmSubmitContent')}</Modal.Body>
        <Modal.Footer>
          <button className="dialogBtn secondary" onClick={handleSubmitReportDialogClose}>
          {t('newReport.confirmSubmitCancel')}
          </button>
          <button className="dialogBtn primary" onClick={handleSubmitReportConfirm}>
          {t('newReport.confirmSubmitYes')}
          </button>
        </Modal.Footer>
      </Modal>

        <LoadingOverlay
          active={localState.isSaveDraftLoading || localState.isSubmitLoading}
          spinner
          text= {localState.isSaveDraftLoading ? t('newReport.savingDraft') : localState.isSubmitLoading ? t('newReport.savingReport') : ''}>
        </LoadingOverlay>

        <LoadingOverlay
          active={localState.isFetchingReportData}
          spinner
          text= {localState.isFetchingReportData ? t('newReport.fetchingReportData') : ''}>
        </LoadingOverlay>
      </div>

      : state.reportLoaded && !state.report ? <span className="textError reportNotExist">{t('newReport.reportNotExist')}</span> : <span></span>
      
    : <Redirect to={ConfigService.getConfig().routes?.login || ''}/>
    }

    <LoadingOverlay
      active={localState.isFetchingReportData}
      spinner
      text= {localState.isFetchingReportData ? t('newReport.fetchingReportData') : ''}>
    </LoadingOverlay>
    </>
  );
};

export default EditReport;

